import { EProductsInsurance, ETax } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { Box, Typography } from '@mui/material'
import { FederalTaxIds } from '@src/types/Constants'
import { ProductDetails, ProductsFundingDetails } from '@src/types/FundingSummaryDto'
import { useTranslation } from 'react-i18next'

interface Props {
  productsFundingDetails: ProductsFundingDetails
}

interface ProductRow {
  labelKey: EProductsInsurance
  details: ProductDetails
}

const sumTaxesByIds = (rows: ProductRow[], taxIds: ETax[]) =>
  rows.reduce(
    (sum, r) =>
      sum + r.details.retentionTaxes.filter((t) => taxIds.includes(t.taxId as ETax)).reduce((s, t) => s + t.amount, 0),
    0,
  )

const dividerSx = {
  borderTop: 1,
  borderColor: 'divider',
  height: 0,
  '@media print': { borderColor: '#999' },
} as const

const RetentionBreakdown = ({ productsFundingDetails }: Props) => {
  const { t } = useTranslation()

  const formatRetentionRate = (rate: number) =>
    t('fundingSummary.ratePercent', {
      rate: (rate * 100).toFixed(2), // retention rates are defined in range 0..1 in LUCAS
    })

  const productRows: ProductRow[] = (
    [
      { labelKey: EProductsInsurance.ExtendedWarranty, details: productsFundingDetails.warrantyDetails },
      { labelKey: EProductsInsurance.CreditInsurance, details: productsFundingDetails.creditInsuranceDetails },
      {
        labelKey: EProductsInsurance.ReplacementOrGapInsurance,
        details: productsFundingDetails.replacementInsuranceDetails,
      },
    ] as const
  ).filter((r): r is ProductRow => r.details !== null)

  const productRetentionSubtotal = productRows.reduce((s, r) => s + r.details.retentionAmount, 0)
  const taxFederalAmount = sumTaxesByIds(productRows, FederalTaxIds)
  const taxQuebecAmount = sumTaxesByIds(productRows, [ETax.QST])
  const retentionTotal = productRetentionSubtotal + taxFederalAmount + taxQuebecAmount
  const hasAnyTax = taxFederalAmount > 0 || taxQuebecAmount > 0
  const amountPaidOut = productsFundingDetails.totalPayoutAmount
  const totalBeforeRetention = amountPaidOut + retentionTotal

  return (
    <Box sx={{ mt: 1, ml: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr 1fr',
          columnGap: 2,
          rowGap: 0.25,
          alignItems: 'baseline',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {t('fundingSummary.retentions')}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>
          {t('fundingSummary.priceCol')}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>
          {t('fundingSummary.rateCol')}
        </Typography>
        <Box />

        {productRows.map(({ labelKey, details }) => (
          <Box key={`row-${labelKey}`} sx={{ display: 'contents' }}>
            <Typography variant="body2">{t(`fundingSummary.${labelKey}`)}</Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {formatCurrency(details.salePrice)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {formatRetentionRate(details.retentionRate)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {formatCurrency(-details.retentionAmount)}
            </Typography>
          </Box>
        ))}

        <Box sx={{ gridColumn: '1 / -1', mt: 0.25, ...dividerSx }} />
        <Typography variant="body2" sx={{ gridColumn: '1 / 4', textAlign: 'right', fontWeight: 600, pt: 0.25 }}>
          {t('fundingSummary.totalBeforeRetention')}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 600, pt: 0.25 }}>
          {formatCurrency(totalBeforeRetention)}
        </Typography>

        {hasAnyTax && (
          <>
            <Typography variant="body2" sx={{ gridColumn: '1 / 4', textAlign: 'right', mt: 1 }}>
              {t('fundingSummary.retentionSubtotal')}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', mt: 1 }}>
              {formatCurrency(-productRetentionSubtotal)}
            </Typography>
          </>
        )}

        {taxFederalAmount > 0 && (
          <>
            <Typography variant="body2" sx={{ gridColumn: '1 / 4' }}>
              {t('fundingSummary.taxFederal')}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {formatCurrency(-taxFederalAmount)}
            </Typography>
          </>
        )}

        {taxQuebecAmount > 0 && (
          <>
            <Typography variant="body2" sx={{ gridColumn: '1 / 4' }}>
              {t('fundingSummary.taxQuebec')}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {formatCurrency(-taxQuebecAmount)}
            </Typography>
          </>
        )}

        <Box sx={{ gridColumn: '1 / -1', mt: 0.25, ...dividerSx }} />
        <Typography variant="body2" sx={{ gridColumn: '1 / 4', textAlign: 'right', fontWeight: 600, pt: 0.25 }}>
          {t('fundingSummary.retentionTotal')}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 600, pt: 0.25 }}>
          {formatCurrency(-retentionTotal)}
        </Typography>

        <Box sx={{ gridColumn: '1 / -1', mt: 1, ...dividerSx }} />
        <Typography variant="body2" sx={{ gridColumn: '1 / 4', textAlign: 'right', fontWeight: 600, pt: 0.5 }}>
          {t('fundingSummary.amountPaidOut')}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 600, pt: 0.5 }}>
          {formatCurrency(amountPaidOut)}
        </Typography>
      </Box>
    </Box>
  )
}

export default RetentionBreakdown
